import { useState } from 'react';
import { useParams, useSearch } from '@tanstack/react-router';
import { Button, Input, Separator, Spinner, Text, TextArea, XStack, YStack } from 'tamagui';
import { useAppToast } from '@mrrmlab/ui';
import { useMailMessage, useMessageTags, useConfirmTag, useRemoveMessageTag, useDeleteTag, useArchiveMail, useReplyToMessage } from '../mail/hooks';
import { useAutoSuggestTags } from '../hooks/use-auto-suggest-tags';
import { useMailModel } from '../hooks/use-mail-model';
import type { MailMessageTag } from '@mrrmlab/api-client';

export function MailReaderRoute() {
  const { accountId } = useParams({ from: '/mail/$accountId/reader' });
  const { folder, uid } = useSearch({ from: '/mail/$accountId/reader' });
  const { data: message, isLoading, error } = useMailMessage(accountId, folder, uid);

  useAutoSuggestTags(message?.id, message?.subject ?? null, message?.bodyText ?? null);
  const { status: modelStatus, downloadProgress } = useMailModel();

  if (isLoading) {
    return (
      <YStack flex={1} alignItems="center" justifyContent="center" padding="$6">
        <Spinner size="large" />
      </YStack>
    );
  }

  if (error || !message) {
    return (
      <YStack padding="$4">
        <Text color="$red10">
          {error instanceof Error ? error.message : 'Nachricht konnte nicht geladen werden.'}
        </Text>
      </YStack>
    );
  }

  const body = message.bodyText ?? stripHtml(message.bodyHtml ?? '');

  return (
    <YStack padding="$4" gap="$3" maxWidth={720}>
      <XStack alignItems="center" gap="$3" flexWrap="wrap">
        <Text fontSize="$6" fontWeight="700" flex={1}>{message.subject || '(kein Betreff)'}</Text>
        <ArchiveButton
          accountId={accountId}
          folder={folder}
          uid={uid}
          archived={message.archived}
        />
      </XStack>

      <YStack gap="$1">
        <MetaRow label="Von" value={message.from} />
        <MetaRow label="An" value={message.to} />
        {message.date ? (
          <MetaRow
            label="Datum"
            value={new Date(message.date).toLocaleString('de-DE', {
              dateStyle: 'medium',
              timeStyle: 'short',
            })}
          />
        ) : null}
      </YStack>

      <TagSection messageId={message.id} />
      {(modelStatus === 'downloading' || modelStatus === 'loading') ? (
        <XStack gap="$2" alignItems="center">
          <Spinner size="small" />
          <Text fontSize="$2" color="$color10">
            {modelStatus === 'downloading'
              ? `KI-Modell wird geladen… ${downloadProgress ?? 0}%`
              : 'KI-Modell wird initialisiert…'}
          </Text>
        </XStack>
      ) : modelStatus === 'error' ? (
        <Text fontSize="$2" color="$red9">KI-Modell konnte nicht geladen werden (siehe Konsole)</Text>
      ) : null}

      <Separator />

      <Text fontSize="$4" lineHeight="$6" style={{ whiteSpace: 'pre-wrap' } as never}>
        {body || '(kein Inhalt)'}
      </Text>

      <Separator />

      <ReplySection
        accountId={accountId}
        folder={folder}
        uid={uid}
        defaultTo={message.from ?? ''}
      />
    </YStack>
  );
}

function ReplySection({
  accountId,
  folder,
  uid,
  defaultTo,
}: {
  accountId: string;
  folder: string;
  uid: number;
  defaultTo: string;
}) {
  const [open, setOpen] = useState(false);
  const [to, setTo] = useState(defaultTo);
  const [body, setBody] = useState('');
  const toast = useAppToast();
  const { mutate, isPending } = useReplyToMessage(accountId, folder, uid);

  if (!open) {
    return (
      <Button size="$3" onPress={() => setOpen(true)}>
        Antworten
      </Button>
    );
  }

  return (
    <YStack gap="$2" borderWidth={1} borderColor="$borderColor" borderRadius="$3" padding="$3">
      <Text fontSize="$3" fontWeight="600">Antwort verfassen</Text>
      <Input
        size="$3"
        value={to}
        onChangeText={setTo}
        placeholder="An"
        autoCapitalize="none"
        keyboardType="email-address"
      />
      <TextArea
        size="$3"
        value={body}
        onChangeText={setBody}
        placeholder="Nachricht…"
        rows={6}
        style={{ minHeight: 120 } as never}
      />
      <XStack gap="$2" justifyContent="flex-end">
        <Button size="$3" chromeless onPress={() => setOpen(false)}>Abbrechen</Button>
        <Button
          size="$3"
          disabled={isPending || !body.trim() || !to.trim()}
          onPress={() =>
            mutate(
              { body, to },
              {
                onSuccess: () => {
                  toast.showSuccess('Antwort gesendet');
                  setOpen(false);
                  setBody('');
                },
                onError: (err) => toast.showError(err),
              },
            )
          }
        >
          {isPending ? 'Sende…' : 'Senden'}
        </Button>
      </XStack>
    </YStack>
  );
}

function ArchiveButton({
  accountId,
  folder,
  uid,
  archived,
}: {
  accountId: string;
  folder: string;
  uid: number;
  archived: boolean;
}) {
  const toast = useAppToast();
  const { mutate, isPending } = useArchiveMail(accountId, folder, uid);

  if (archived) {
    return (
      <Button size="$3" disabled chromeless color="$green10">
        Archiviert ✓
      </Button>
    );
  }

  return (
    <Button
      size="$3"
      onPress={() =>
        mutate(undefined, {
          onSuccess: (result) => {
            toast.showSuccess(
              result.alreadyArchived
                ? `Bereits archiviert (Paperless #${result.paperlessDocumentId})`
                : `Mail archiviert (Paperless #${result.paperlessDocumentId})`,
            );
          },
          onError: (err) => toast.showError(err),
        })
      }
      disabled={isPending}
    >
      {isPending ? 'Archiviere…' : 'Archivieren'}
    </Button>
  );
}

function TagSection({ messageId }: { messageId: string }) {
  const { data: tags, isLoading } = useMessageTags(messageId);
  const { mutate: confirm } = useConfirmTag();
  const { mutate: remove } = useRemoveMessageTag();
  const { mutate: deleteTag } = useDeleteTag();

  if (isLoading || !tags?.length) return null;

  return (
    <XStack flexWrap="wrap" gap="$2" alignItems="center">
      {tags.map((tag) => (
        <TagChip
          key={tag.id}
          tag={tag}
          onConfirm={() => confirm({ messageId, tagId: tag.tagId })}
          onRemove={() =>
            tag.status === 'suggested'
              ? deleteTag(tag.tagId)
              : remove({ messageId, tagId: tag.tagId })
          }
        />
      ))}
    </XStack>
  );
}

function TagChip({ tag, onConfirm, onRemove }: { tag: MailMessageTag; onConfirm: () => void; onRemove: () => void }) {
  const confirmed = tag.status === 'confirmed';
  return (
    <XStack
      borderRadius="$10"
      paddingHorizontal="$3"
      paddingVertical="$1"
      gap="$2"
      alignItems="center"
      backgroundColor={confirmed ? '$green3' : '$yellow3'}
      borderWidth={1}
      borderColor={confirmed ? '$green7' : '$yellow7'}
    >
      <Text fontSize="$3" color={confirmed ? '$green11' : '$yellow11'}>
        {tag.tag?.name ?? '…'}
      </Text>
      {!confirmed ? (
        <Button size="$1" chromeless onPress={onConfirm} color="$green10">✓</Button>
      ) : null}
      <Button size="$1" chromeless onPress={onRemove} color="$color10">✕</Button>
    </XStack>
  );
}

function MetaRow({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value) return null;
  return (
    <Text fontSize="$3" color="$color10">
      <Text fontWeight="600">{label}: </Text>
      {value}
    </Text>
  );
}

function stripHtml(html: string): string {
  return html
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/\s{2,}/g, ' ')
    .trim();
}
